// Client-side pluralization, following the approach of the i18n-js package: a registry of per-locale
// pluralizers with a sensible default. Rules follow the Unicode CLDR plural categories for INTEGER
// counts (our `count` is always an integer). Every pluralizer appends `other` as a fallback so that a
// category missing from the translations (e.g. a locale file that only defines `one`/`other`) degrades
// gracefully to `other` instead of surfacing a "[missing]" string.

const OTHER = 'other';

// count === 1 → one (en, de, es, it, nl, pt, scandinavian, …). This is also the global default.
const oneOther = (count) => (Math.abs(count) === 1 ? ['one', OTHER] : [OTHER]);

// 0 and 1 → one (French, Armenian, …).
const frenchOther = (count) => (Math.abs(count) < 2 ? ['one', OTHER] : [OTHER]);

// No plural distinction (Japanese, Chinese, Korean, Vietnamese, Thai, Indonesian, …).
const otherOnly = () => [OTHER];

// East-Slavic: Russian, Ukrainian, Belarusian.
const eastSlavic = (count) => {
    const n = Math.abs(count);
    const mod10 = n % 10;
    const mod100 = n % 100;

    if (mod10 === 1 && mod100 !== 11) {
        return ['one', OTHER];
    }
    if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) {
        return ['few', OTHER];
    }

    return ['many', OTHER];
};

// Polish.
const polish = (count) => {
    const n = Math.abs(count);
    const mod10 = n % 10;
    const mod100 = n % 100;

    if (n === 1) {
        return ['one', OTHER];
    }
    if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) {
        return ['few', OTHER];
    }

    return ['many', OTHER];
};

// Czech, Slovak.
const czechSlovak = (count) => {
    const n = Math.abs(count);

    if (n === 1) {
        return ['one', OTHER];
    }
    if (n >= 2 && n <= 4) {
        return ['few', OTHER];
    }

    return ['many', OTHER];
};

// Romanian.
const romanian = (count) => {
    const n = Math.abs(count);
    const mod100 = n % 100;

    if (n === 1) {
        return ['one', OTHER];
    }
    if (n === 0 || (mod100 >= 1 && mod100 <= 19)) {
        return ['few', OTHER];
    }

    return [OTHER];
};

// Lithuanian.
const lithuanian = (count) => {
    const n = Math.abs(count);
    const mod10 = n % 10;
    const mod100 = n % 100;

    if (mod10 === 1 && !(mod100 >= 11 && mod100 <= 19)) {
        return ['one', OTHER];
    }
    if (mod10 >= 2 && mod10 <= 9 && !(mod100 >= 11 && mod100 <= 19)) {
        return ['few', OTHER];
    }

    return [OTHER];
};

// Arabic.
const arabic = (count) => {
    const n = Math.abs(count);
    const mod100 = n % 100;

    if (n === 0) {
        return ['zero', OTHER];
    }
    if (n === 1) {
        return ['one', OTHER];
    }
    if (n === 2) {
        return ['two', OTHER];
    }
    if (mod100 >= 3 && mod100 <= 10) {
        return ['few', OTHER];
    }
    if (mod100 >= 11 && mod100 <= 99) {
        return ['many', OTHER];
    }

    return [OTHER];
};

// Language (not locale) → pluralizer. Keys are ISO 639-1 codes; the region subtag is stripped in get().
const RULES = {
    // one/other (n === 1)
    en: oneOther, de: oneOther, es: oneOther, it: oneOther, nl: oneOther, pt: oneOther,
    sv: oneOther, da: oneOther, nb: oneOther, nn: oneOther, no: oneOther, fi: oneOther,
    et: oneOther, ca: oneOther, gl: oneOther, eu: oneOther, el: oneOther, bg: oneOther,
    hu: oneOther, tr: oneOther,
    // 0 and 1 → one
    fr: frenchOther, hy: frenchOther,
    // no plural distinction
    ja: otherOnly, zh: otherOnly, ko: otherOnly, vi: otherOnly, th: otherOnly,
    id: otherOnly, ms: otherOnly,
    // one/few/many and friends
    ru: eastSlavic, uk: eastSlavic, be: eastSlavic,
    pl: polish, cs: czechSlovak, sk: czechSlovak,
    ro: romanian, lt: lithuanian, ar: arabic,
};

export class Pluralization {
    constructor(i18n) {
        this.i18n = i18n;
        this.registry = {...RULES};
    }

    // Returns a pluralizer `(i18n, count) => orderedKeys`. Resolves by the given locale, then the
    // i18n default locale, then the built-in `oneOther` fallback (correct for most languages we serve).
    get(locale) {
        const language = (locale || this.i18n.locale || '').split('-')[0].toLowerCase();
        const rule = this.registry[language] || oneOther;

        return (_i18n, count) => rule(count);
    }

    // Allow overriding/adding a rule at runtime (mirrors i18n-js). `language` is an ISO 639-1 code.
    register(language, pluralizer) {
        this.registry[language] = pluralizer;
    }
}
